import {Injectable} from '@angular/core';
import {DatePipe} from '@angular/common';
import {MatDialogConfig} from '@angular/material/dialog';
import {Router, ActivatedRoute} from '@angular/router';
import {of, Observable, combineLatest, BehaviorSubject, Subject, throwError} from 'rxjs';
import {shareReplay, distinctUntilChanged, map, switchMap, take, filter, tap, catchError} from 'rxjs/operators';

import {HoldingStorageObjectSearchOptionsRequest} from 'src/app/shared/models/HoldingStorageObjectSearchOptionsRequest.model';
import {SearchOptionsResponse} from 'src/app/shared/models/SearchOptionsResponse.model';
import {TerraUtils} from 'src/app/shared/TerraUtils';
import {HoldingStorageObjectReqRes} from 'src/app/shared/models/StorageObjectReqRes.model';
import {StorageObjectDataService} from 'src/app/services/shared/storage-object-data.service';
import {RoutingService} from 'src/app/services/routing.service';
import HoldingDiscriminator from 'src/app/shared/enums/HoldingDiscriminator.enum';
import {LpFundDataService} from 'src/app/services/lp/lp-fund-data.service';
import {TransactionStatus} from '../../models/transactionStatus.enum';
import {DistributionTransactionPurpose} from 'src/app/shared/enums/DistributionTransactionPurpose.enum';
import {TerraNumberPipe} from 'src/app/shared/pipes/TerraNumber.pipe';
import {LpHoldingDataService} from 'src/app/services/lp/lp-holding-data.service';
import HoldingStatus from '../../../shared/enums/HoldingStatus.enum';
import {LpAssetReqRes} from '../../assets/LpAssetReqRes.model';
import {LpFundReqRes} from '../../funds/LpFundReqRes.model';
import {StorageObjectPolicyReqRes} from 'src/app/shared/models/StorageObjectPolicyReqRes.model';
import moment from 'moment';
import {PickerResponse} from 'filestack-js';
import MetaFileLink from 'src/app/models/metaFileLink.model';
import {LoggerService} from 'src/app/shared/errors/logger.service';
import { UserService } from 'src/app/services/shared/user.service';
import { AlertDialogParams } from 'src/app/shared/components/alert-dialog/alert-dialog.component';
import { DialogService } from 'src/app/services/dialog.service';
import { LpInvestingEntityDataService } from 'src/app/services/lp/lp-investing-entity-data.service';
import { InvestingEntityReqRes } from 'src/app/dashboard/models/InvestingEntityReqRes.model';

@Injectable()
export abstract class LpHoldingService {

  readonly abstract holdingDiscriminator: HoldingDiscriminator;
  abstract status$: Observable<HoldingStatus>;

  holdingId$: Observable<number>;
  holdingDetails$: Observable<LpAssetReqRes | LpFundReqRes>;

  private filestackPolicy$ = new BehaviorSubject<StorageObjectPolicyReqRes>(null);
  isLoaderDocument$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(true);
  readonly maxDisplayNameLength = 96;

  originFundId$: Observable<number> = this._route
    .queryParams.pipe(map(query => +query[this._routingService.originFundParamName] || null));

  originFundBasicInfo$ = this.originFundId$.pipe(switchMap(fundId => {
    if (!fundId) {
      return of(null);
    } else {
      return this._lpFundDataService.getById(fundId, true);
    }
  }));

  reportId$: Observable<number>;
  investingEntities$: Observable<InvestingEntityReqRes[]>;

  transactionsExcelUrl$: Observable<string>;

  readonly transactionStatus = TransactionStatus;
  readonly distributionType = DistributionTransactionPurpose;

  // services
  constructor(
    private _storageObjectDataService: StorageObjectDataService,
    private _routingService: RoutingService,
    private _router: Router,
    private _route: ActivatedRoute,
    private _lpFundDataService: LpFundDataService,
    private _datePipe: DatePipe,
    private _terraNumberPipe: TerraNumberPipe,
    private _lpHoldingDataService: LpHoldingDataService,
    private _logger: LoggerService,
    private _userService: UserService,
    private _dialogService: DialogService,
    private _lpInvestingEntityDataService: LpInvestingEntityDataService
  ) {
    this.initAllProperies();
  }

  private initAllProperies() {
    this.holdingId$ = this._route.params.pipe(
      distinctUntilChanged(),
      map(params => +params.holdingId),
      shareReplay(1)
    );

    this.transactionsExcelUrl$ = this.holdingId$.pipe(
      take(1),
      switchMap(holdingId => this._lpHoldingDataService.exportTransactions(holdingId)),
      shareReplay(1)
    );

    this.investingEntities$ = this.holdingId$.pipe(
      take(1),
      switchMap(holdingId => this._lpInvestingEntityDataService.getInvestingEntitiesByHoldingId(holdingId)),
      shareReplay(1)
    );

  }

  getDocuments(filterText: string, fromDate: Date, toDate: Date, orderBy:string, sortOrder: 'asc' | 'desc') {
    this.isLoaderDocument$.next(true);
    return this.holdingId$.pipe(switchMap(holdingId => {
        if (holdingId === TerraUtils.consts.EXAMPLE_ASSET_ID) {
          const exampleDocs = new SearchOptionsResponse<HoldingStorageObjectReqRes>();
          exampleDocs.rows = [];
          exampleDocs.totalCount = 0;
          return of(exampleDocs);
        } else {
          const searchOptions = new HoldingStorageObjectSearchOptionsRequest();
          searchOptions.pageSize = 1000;
          searchOptions.holdingId = holdingId;
          searchOptions.filter = filterText || '';
          searchOptions.fromDate = fromDate || null;
          searchOptions.toDate = toDate || null;
          searchOptions.searchGlobally = true;
          searchOptions.orderBy = orderBy || 'CreateTimestamp';
          searchOptions.sortOrder = sortOrder || 'asc';
          return this._storageObjectDataService.getStorageObjects(searchOptions).pipe(shareReplay(1));
        }
      }),
      tap(() => this.isLoaderDocument$.next(false))
    );
  }

  previewHoldingDocument(fileToPreview: HoldingStorageObjectReqRes) {
    combineLatest([this.holdingId$, this.originFundId$])
      .pipe(take(1)).subscribe(([holdingId, originFundId]) => {
      this._router.navigate(
        [this._routingService.storageObjectPreviewPage(holdingId, this.holdingDiscriminator, fileToPreview.id)],
        {
          queryParams: {origin: originFundId}
        });
    });
  }

  downloadDocument(storageObject: HoldingStorageObjectReqRes) {
    this._storageObjectDataService.getDownloadToken(storageObject.id).subscribe();
  }

  setTransactionDialogConfig(config: MatDialogConfig) {
    config.disableClose = false;
    config.closeOnNavigation = true;
    config.autoFocus = true;
    config.width = '100vw';
    config.maxWidth = '600px';
    config.height = '100vh';
    config.maxHeight = '800px';
  }

  toTag(content: string, tag: string) {
    return `<${tag}>${content}</${tag}>`;
  }

  getHoldingCashFlowChartData() {
    return this.holdingId$.pipe(
      take(1),
      filter(id => id !== TerraUtils.consts.EXAMPLE_ASSET_ID),
      switchMap(id => this._lpHoldingDataService.getCashFlowChartData(id))
    );
  }


  getFilestackPolicy(): Observable<StorageObjectPolicyReqRes> {
    if (!this.filestackPolicy$.value || this.filestackPolicy$.value.expiry <= new Date()) {
      return this._storageObjectDataService.getFilestackPolicy().pipe(
        map(policy => {
          policy.expiry = moment(new Date()).add(50, 'minute').toDate();
          return policy;
        }),
        tap(policy => {
          this.filestackPolicy$.next(policy);
        }));
    } else {
      return this.filestackPolicy$;
    }
  }

  getQuotaRefreshed() {
    return this._storageObjectDataService.getQuota();
  }

  /// Returns the number of files created
  createFiles(pickerResponse: PickerResponse, investingEntityId: number): Observable<HoldingStorageObjectReqRes[]> {
    let model: HoldingStorageObjectReqRes[];
    this.isLoaderDocument$.next(true);
    return this.holdingId$.pipe(
      take(1),
      switchMap(holdingId => {
        model = this.generateCreateFilesSubmitModel(pickerResponse, holdingId, 0); // this.currentFolderId$.value);
        return this._storageObjectDataService.createFilesLp(model, investingEntityId);
      }),
      tap(() => {
        this.isLoaderDocument$.next(false);
      }),
      catchError(error => {
        this.isLoaderDocument$.next(false);
        return throwError(error);
      })
    );
  }

  private generateCreateFilesSubmitModel(pickerResponse: PickerResponse, holdingId: number, containingFolderId: number): HoldingStorageObjectReqRes[] {
    const model = new Array<HoldingStorageObjectReqRes>();
    pickerResponse.filesUploaded.forEach(file => {
      const storageObject = new HoldingStorageObjectReqRes();
      storageObject.storageObjectParentId = containingFolderId;
      storageObject.holdingId = holdingId;
      storageObject.filestackHandle = file.handle;
      storageObject.filestackUrl = file.url;
      storageObject.filestackSource = file.source;
      storageObject.displayName = file.filename?.length > this.maxDisplayNameLength ? this.getShortenDisplayName(file.filename) : file.filename;
      storageObject.metaFileLink = new MetaFileLink();
      storageObject.metaFileLink.title = file.filename;
      storageObject.metaFileLink.mediaType = file.mimetype;
      storageObject.metaFileLink.sizeB = file.size;
      model.push(storageObject);
    });
    return model;
  }

  /**
   * Shorten a long file name to the maximum allowed. maximum file name length defined in maxDisplayNameLength
   * @param originalDisplayName long file name
   */
  private getShortenDisplayName(originalDisplayName: string): string {
    try {
      if (originalDisplayName.length > 0) {
        const nameSplittedByDot = originalDisplayName.split('.');
        if (nameSplittedByDot.length > 1) {
          const extension = '.' + nameSplittedByDot[nameSplittedByDot.length - 1];
          let shortName = originalDisplayName.split(extension)?.shift();
          shortName = shortName.substring(0, this.maxDisplayNameLength - extension.length);
          return shortName + extension;
        } else {
          return originalDisplayName.substring(0, this.maxDisplayNameLength);
        }
      }
      return '';
    } catch (ex) {
      this._logger.error(`An error occurred while trying to shorten file name. original file name: ${originalDisplayName}`, ex);
      return originalDisplayName.substring(0, this.maxDisplayNameLength);
    }
  }

  /**
   * Checks if the username and the email where the LP received a link to the holding match
   * @param userName
   * @param holdingDiscriminator
   * @returns
   */
  checkSameEmails(userName:string, holdingDiscriminator:HoldingDiscriminator):boolean{

    const lpReceiveEmail = this._route.snapshot?.queryParams?.email ?? "";
    const sameEmail = !lpReceiveEmail || userName === lpReceiveEmail.toLowerCase();

    if(!sameEmail){
      const alertDialogParams = new AlertDialogParams();
      alertDialogParams.title = `${holdingDiscriminator === HoldingDiscriminator.Fund ? 'Fund' : 'Asset'} Access Denied`;
      alertDialogParams.description = `The email address you are logged in with does not match the email address associated with your invitation link. Please contact your GP and validate the email address that was linked to this investment.

Your email address: <strong>${userName}</strong>
The address associated with this link: <strong>${lpReceiveEmail}</strong>`;
      this._dialogService.alertDialog(alertDialogParams).afterClosed()
          .subscribe(() => this._router.navigateByUrl(this._routingService.lpHomePage));
    }
    return sameEmail;
  }
}
